import React from 'react';
import { useCart } from '../../context/CartContext';
import { PaystackButton } from 'react-paystack';
import toast from 'react-hot-toast';
import paymentsService from '../../services/PaymentServices';
import utils from '../../utils';
import { useNavigate } from 'react-router-dom';
import Loading from '../feedback/Loading';

const MAX_RETRIES = 7;
const RETRY_DELAY_MS = 2000; // 2 seconds between retries

function PaymentInfo({ loading, setError, setLoading }) {
    const { payment, resetCart, customerProfile } = useCart();
    const navigate = useNavigate();

    const config = {
        reference: payment?.transaction_reference,
        email: customerProfile?.email,
        amount: parseInt(payment?.total) * 100,
        currency: "KES",
        publicKey: process.env.REACT_APP_PAYSTACK_PUB_KEY,
    };

    // Recursive retry function with max attempts
    const checkPaymentWithRetry = async (reference, loadingToast, attempt = 1) => {
        if (attempt > MAX_RETRIES) {
            toast.error("Payment verification timed out. Please contact support.", {
                id: loadingToast,
            });
            setLoading(false);
            setError("Payment could not be verified after multiple attempts.");
            return;
        }

        try {
            const res = await paymentsService.confirmPayment(reference.reference);
            if (res.status === 200) {
                toast.success("Payment Confirmed!", { id: loadingToast });
                resetCart();
                navigate('/cart/checkout/complete');
                setLoading(false);
            } else {
                // Retry after delay
                setTimeout(() => {
                    checkPaymentWithRetry(reference, loadingToast, attempt + 1);
                }, RETRY_DELAY_MS);
            }
        } catch (err) {
            console.error("Retry error:", err);
            let errorMessage = 'Something went wrong';

            if (err.response?.status === 400 && Array.isArray(err.response.data?.details)) {
                const messages = err.response.data.details.map(d => {
                    const fieldName = d.field ? `${d.field.charAt(0).toUpperCase() + d.field.slice(1)}` : 'Field';
                    return `${fieldName}: ${d.message}`;
                });
                errorMessage = messages.join('; ') || 'Validation failed';
            } else if (err.response?.status === 400 && err.response.data) {
                const data = err.response.data;
                const fieldErrors = Object.entries(data)
                    .flatMap(([field, msgs]) =>
                        (Array.isArray(msgs) ? msgs : [msgs])
                            .filter(msg => typeof msg === 'string')
                            .map(msg => `${field.charAt(0).toUpperCase() + field.slice(1)}: ${msg}`)
                    );
                errorMessage = fieldErrors.join('; ') || 'Invalid input';
            } else if (err.request) {
                errorMessage = 'Network error — please check your connection';
            } else {
                errorMessage = err.message || 'Something went wrong';
            }

            // Only show error on final failure
            if (attempt >= MAX_RETRIES) {
                setError(errorMessage);
                toast.error(utils.toastError || errorMessage, { id: loadingToast });
                setLoading(false);
            } else {
                // Quietly retry on non-final errors
                setTimeout(() => {
                    checkPaymentWithRetry(reference, loadingToast, attempt + 1);
                }, RETRY_DELAY_MS);
            }
        }
    };

    const onSuccess = (reference) => {
        console.log("Paystack success:", reference);
        setLoading(true);
        const loadingToast = toast.loading("Verifying your payment…");
        checkPaymentWithRetry(reference, loadingToast, 1);
    };

    const onClose = () => {
        console.log('Paystack modal closed');
        setLoading(false);
        toast.error("Payment was not completed.");
    };

    const componentProps = {
        ...config,
        text: loading ? "Processing Payment..." : "Finish Payment",
        onSuccess,
        onClose,
    };

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <PaystackButton
                    {...componentProps}
                    disabled={loading}
                    className={`w-full font-bold py-3 px-4 rounded-lg text-center transition shadow-md
            ${loading
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-secondary text-white hover:bg-prinary cursor-pointer'
                        }`}
                />
            )}
        </div>
    );
}

export default PaymentInfo;